/**
 * The corrected server clock, as a React value.
 *
 * `Date.now()` is banned outside `lib/clock/` (D9, eslint), so anything that
 * needs "what time is it" reads it here — and it reads
 * `performance.now() + offset`, which the user cannot change and a laptop wake
 * does not reset.
 *
 * **It re-renders the component that calls it, so call it as far down the tree
 * as possible.** A `useServerNow()` at the top of the market page would
 * reconcile the whole page four times a second for the benefit of two progress
 * bars. The countdown does not use this at all: it holds its own `critical`-lane
 * subscription and writes to the DOM, because at 10 Hz even a leaf re-render is
 * competing with the book flush (plan §2.4).
 *
 * The tick runs on the shared scheduler's `default` lane rather than on a
 * `setInterval`, for two reasons: a hidden tab throttles rAF to zero and stops
 * paying for a clock nobody is watching, and there is one frame loop in this
 * client rather than one plus a timer.
 */

import { useEffect, useState } from 'react'
import { sharedClock } from '@/lib/clock'
import { sharedScheduler } from '@/lib/raf'

/** 4 Hz. Fine enough that a 13-second determinate bar moves in 52 steps rather
 *  than 13, coarse enough that it is not the countdown. */
export const DEFAULT_TICK_MS = 250

export function useServerNow(intervalMs: number = DEFAULT_TICK_MS): number {
  const clock = sharedClock()
  const [nowMs, setNowMs] = useState(() => clock.now())

  useEffect(() => {
    let lastMs = -Infinity
    return sharedScheduler().subscribe((frame) => {
      if (frame.timeMs - lastMs < intervalMs) return
      lastMs = frame.timeMs
      setNowMs(clock.now())
    }, 'default')
  }, [clock, intervalMs])

  return nowMs
}
